import { useQuery } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api/client';
import type { EntityKind } from '@/features/recents/recent-entities';

/**
 * What the operator typed. Detecting this client-side lets the palette show
 * "jump straight to this ObjectId" as the top hit before any network round trip,
 * which is the difference between one keystroke and three during an incident.
 */
export type QueryKind = 'objectId' | 'inviteCode' | 'email' | 'phone' | 'text';

const OBJECT_ID = /^[a-f0-9]{24}$/i;
const INVITE_CODE = /^[A-Z0-9]{5,10}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[\d\s().-]{7,20}$/;

export function detectQueryKind(raw: string): QueryKind {
  const value = raw.trim();
  if (OBJECT_ID.test(value)) return 'objectId';
  if (EMAIL.test(value)) return 'email';
  // Invite codes are checked before phone so an all-digit code is not misread.
  if (INVITE_CODE.test(value.toUpperCase()) && /[A-Z]/i.test(value)) return 'inviteCode';
  if (PHONE.test(value) && /\d{7,}/.test(value.replace(/\D/g, ''))) return 'phone';
  return 'text';
}

export interface SearchHit {
  kind: EntityKind;
  id: string;
  /** Primary label, e.g. league name or username. */
  title: string;
  /** Secondary identifier, e.g. invite code, email, or member count. */
  subtitle?: string;
  to: string;
}

export interface SearchResponse {
  groups: { kind: EntityKind; label: string; hits: SearchHit[] }[];
  /** Set when the query resolved to exactly one entity by identifier. */
  exact?: SearchHit;
}

export function useGlobalSearch(term: string, enabled: boolean) {
  const trimmed = term.trim();

  return useQuery({
    queryKey: ['global-search', trimmed] as const,
    queryFn: ({ signal }) =>
      apiRequest<SearchResponse>('/api/search', {
        searchParams: { q: trimmed, kind: detectQueryKind(trimmed) },
        signal,
      }),
    enabled: enabled && trimmed.length >= 2,
    // Search is latency-sensitive and cheap to repeat; keep results briefly.
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

export const QUERY_KIND_HINT: Record<QueryKind, string | null> = {
  objectId: 'Looks like an ObjectId',
  inviteCode: 'Looks like a league invite code',
  email: 'Looks like an email address',
  phone: 'Looks like a phone number',
  text: null,
};
